/*
 * Copyright 2025 Intuition Robotics ltd
 */
import * as React from "react";
import {CSSProperties} from "react";
import {
    I18n,
    SimpleDatePicker
} from "simple-ts-date-picker";
import {COLORS} from "@res/colors";
import {BadImplementationException} from "@intuitionrobotics/ts-common";

type State = {
    dateValue: Date,
    month: { name: string, index: number }
    year: number,
    today: Date
}

type Props = {
    onFilter: (date: Date) => void
    minDate?: Date
    maxDate?: Date
    initialDate?: Date
}

const style: CSSProperties = {
    textAlign: 'center',
    fontWeight: "bold",
    backgroundColor: COLORS.white,
    color: COLORS.grays_dark
};
const arrowStyle = {
    color: COLORS.grays_4,
    backgroundColor: COLORS.white,
    cursor: 'pointer'
};

export class Calendar
    extends React.Component<Props, State> {

    constructor(props: Props) {
        super(props);
        const monthsArray = ['Jan', 'Feb', 'March', 'April', 'May', 'June', 'July', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec'];
        const date = props.initialDate ?? new Date();
        this.state = {
            dateValue: date,
            month: {name: monthsArray[date.getMonth()], index: date.getMonth()},
            year: date.getFullYear(),
            today: new Date()
        };
    }

    getClickedStyle = (day: Date) => {
        let color = COLORS.grays_dark;
        const minDate = this.props.minDate ? this.props.minDate : new Date(1970, 0, 1);
        const maxDate = this.props.maxDate ?? new Date(9999, 11, 31);

        if (this.state.dateValue.getDate() === day.getDate() && this.state.dateValue.getMonth() === day.getMonth())
            color = 'white';
        if (day > maxDate || day < minDate)
            color = COLORS.grays_2;
        const style = {
            background: this.state.dateValue.getDate() === day.getDate() && this.state.dateValue.getMonth() === day.getMonth() ? COLORS.semantic_blue : 'white',
            color,
            cursor: this.state.dateValue.getDate() === day.getDate() && this.state.dateValue.getMonth() === day.getMonth() ? 'default' : 'pointer'
        };
        return <td onClick={() => {
            if (day <= maxDate && day >= minDate) {
                this.setState({dateValue: day});
                this.props.onFilter(day);
            }
        }} style={style}>{day.getDate()}</td>;
    };


    calculatePreviousMonth = (days: I18n) => {
        const index = this.state.month.index;
        if (index === -1)
            throw new BadImplementationException('you cant have a month in state that does not exist in months list');
        if (index === 0)
            return days.months[11];
        return days.months[index - 1];
    };

    renderCalHeader = (days: I18n, date: Date, onScreenChange: (screen: Date) => void) => {
        return <tr>
            {this.renderMonth(days, onScreenChange, date)}
            {this.renderYear(days, onScreenChange, date)}
        </tr>
    };

    private renderMonth(days: I18n, onScreenChange: (screen: Date) => void, date: Date) {
        return <>
            <th style={arrowStyle} onClick={() => {
                const previousMonth = this.calculatePreviousMonth(days);
                this.setState({month: {name: previousMonth, index: days.months.indexOf(previousMonth)}});
                onScreenChange(new Date(date.getFullYear(), days.months.indexOf(previousMonth)));
            }}>
                <a>
                    ‹
                </a>
            </th>
            <th style={style}>
                {this.state.month.name}
            </th>
            <th style={arrowStyle} onClick={() => {
                const nextMonth = this.calculateNextMonth(days);
                this.setState({month: {name: nextMonth, index: days.months.indexOf(nextMonth)}});
                onScreenChange(new Date(date.getFullYear(), days.months.indexOf(nextMonth)));
            }}>
                <a>
                    ›
                </a>
            </th>
        </>;
    }

    private renderYear(days: I18n, onScreenChange: (screen: Date) => void, date: Date) {
        return <>
            <th style={arrowStyle} onClick={() => {
                const previousYear = this.state.year - 1;
                this.setState({year: previousYear});
                onScreenChange(new Date(previousYear, this.state.month.index));
            }}>
                <a>
                    ‹
                </a>
            </th>
            <th style={style}>
                {this.state.year}
            </th>
            <th style={arrowStyle} onClick={() => {
                const nextYear = this.state.year + 1;
                if (this.props.maxDate && nextYear > this.props.maxDate.getFullYear()) return;
                this.setState({year: nextYear});
                onScreenChange(new Date(nextYear, this.state.month.index));
            }}>
                <a>
                    ›
                </a>
            </th>
        </>;
    }

    renderDayAbbrev = (days: I18n) => {
        return <tr>
            {days.weekdaysShort.map((_day, i) => <th key={i} style={{color: COLORS.semantic_blue}}>{_day}</th>)}
        </tr>;
    };

    calculateNextMonth = (days: I18n) => {
        const index = this.state.month.index;
        if (index === -1)
            throw new BadImplementationException('you cant have a month in state that does not exist in months list');
        if (index === days.months.length - 1)
            return days.months[0];
        return days.months[index + 1];
    };

    render() {
        return <SimpleDatePicker
            value={this.state.dateValue}
            renderDay={day => this.getClickedStyle(day)}
            renderAbbreviations={day => this.renderDayAbbrev(day)}
            renderHeader={(day, date, onScreenChange) => this.renderCalHeader(day, date, onScreenChange)}
            onChange={(value) => this.setState({dateValue: value})}/>;
    }
}
